/*
    This the composition preview.
    */

import React, { Component } from 'react'
import classNames from 'classnames'
import { bool, func, number, string } from 'prop-types'
import * as Sentry from '@sentry/react'

import Renderer from 'Renderer'
import { SENTRY_BREADCRUMB_CATEGORY } from 'Renderer/constants'

import { compositionType, debugSettingsType, previewSettingsType, dimensionsType } from 'models/propTypes'

import * as RenderItemTypes from 'constants/renderItemTypes'
import { STATE_ERROR } from 'constants/stateTypes'
import { withTranslation, Trans } from 'react-i18next'
import CompositionRenderItemContainer from 'containers/CompositionRenderItemContainer'
import CompositionStackableSurfaceItemContainer from 'containers/CompositionStackableSurfaceItemContainer'
import WallPaintPinContainer from 'containers/WallPaintPinContainer'
import MissingFeatureModal from 'components/MissingFeatureModal'
import DirectInteractionLayer from 'components/design/DirectInteractionLayer'
import BouncingBallLoader from 'components/uiElements/BouncingBallLoader'
import MemoryCharts from 'components/MemoryCharts'
import SugarcubeContext from 'apps/SugarcubeContext'
import { getRenderer } from 'models/refStore/selectors'
import Button from '@ingka/button'
import SSRIcon from '@ingka/ssr-icon'
import arrowRight from '@ingka/ssr-icon/paths/arrow-right'
import visibilityHide from '@ingka/ssr-icon/paths/visibility-hide'
import visibilityShow from '@ingka/ssr-icon/paths/visibility-show'
import cross from '@ingka/ssr-icon/paths/cross'
import SkipLinks from 'components/SkipLinks'
import { BROWSE, DESIGN, HEADER, NAVIGATION } from 'components/SkipLinks/constants'
import { getClientEventCoords } from 'utils/events'
import Sweep from './Sweep'

require('./Preview.scss')

const stopPropagationFunc = (e) => e.stopPropagation()

// TODO: clean up where we're using state and where
// we're using props. Right now it's a confusing mix.
// TODO: we can probably get rid of derived state
// and use a better abstraction
// TODO: we probably don't need to pass in composition

class Preview extends Component {
    static generateImageData(imageUrl) {
        // Load wallart and return the resulting imageData
        const artStagingCanvas = document.createElement('canvas')
        const artStagingContext = artStagingCanvas.getContext('2d')
        const artImg = new Image()
        // eslint-disable-next-line i18next/no-literal-string
        artImg.crossOrigin = 'anonymous'
        artImg.src = imageUrl

        return new Promise((resolve) => {
            artImg.onload = () => {
                artStagingCanvas.width = artImg.width
                artStagingCanvas.height = artImg.height
                artStagingContext.drawImage(artImg, 0, 0)
                const artImageData = artStagingContext.getImageData(0, 0, artImg.width, artImg.height)

                resolve(artImageData)
            }
        })
    }

    constructor(props) {
        super(props)

        this.cacheImages = this.cacheImages.bind(this)
        this.triggerRaycast = this.triggerRaycast.bind(this)
        this.onMouseDown = this.onMouseDown.bind(this)
        this.onMouseLeave = this.onMouseLeave.bind(this)
        this.onWindowMouseDown = this.onWindowMouseDown.bind(this)
        this.onKeyDown = this.onKeyDown.bind(this)
        this.onSweepButtonMouseOver = this.onSweepButtonMouseOver.bind(this)
        this.onSweepButtonMouseOut = this.onSweepButtonMouseOut.bind(this)
        this.onContextCreationError = this.onContextCreationError.bind(this)

        this.sceneContainerRef = React.createRef()
        this.canvasRef = React.createRef()

        this.state = {
            composition: props.composition,
            coords: null,
            underCursorItemId: null,
            underCursorStackableSurfaceId: null,
            stackableCursorMouseoverCoords: null,
            isInvalidForStackable: false,
            debugUseMultiShadow: false, // Track change to reload renderer
            revealDots: false,
            showSweepToast: false,
        }
    }

    componentDidMount() {
        this.cacheImages()

        const canvas = this.canvasRef.current
        canvas.addEventListener('webglcontextlost', this.props.onContextLost, false)
        canvas.addEventListener('webglcontextcreationerror', this.onContextCreationError, false)
        canvas.addEventListener('webglcontextrestored', this.props.onContextRestored, false)
    }

    componentDidUpdate(prevProps, prevState) {
        this.cacheImages(prevState)
        const renderer = getRenderer()

        if (!renderer) {
            const {
                composition,
                debugSettings,
                previewSettings,
                onRendererReady,
                onUpdateCompositionSaveData,
                onFirstRender,
                onWallDataReady,
                maxTextureSize,
                updateHiddenSurfaces,
                onInitializationFailure,
            } = this.props
            if (composition) {
                const { scene, savedProperties } = composition
                const canvas = this.canvasRef.current

                Renderer.newInstance({
                    canvas,
                    scene,
                    debugSettings,
                    previewSettings,
                    onFirstRender,
                    onReady: onRendererReady,
                    saveData: savedProperties,
                    onUpdateSaveData: onUpdateCompositionSaveData,
                    onWallDataReady,
                    maxTextureSize,
                    updateHiddenSurfaces,
                    onInitializationFailure,
                })
                this.props.newRenderer()
            }
        }

        if (renderer && this.props.debugSettings !== prevProps.debugSettings) {
            renderer.updateDebugSettings(this.props.debugSettings)
        }
        if (renderer && this.props.previewSettings !== prevProps.previewSettings) {
            renderer.updateSettings(this.props.previewSettings)
        }
        if (this.props.waitingSurfacesInteraction !== prevProps.waitingSurfacesInteraction) {
            if (this.props.waitingSurfacesInteraction) {
                window.addEventListener('mousedown', this.onWindowMouseDown)
                document.addEventListener('keydown', this.onKeyDown)
            } else {
                window.removeEventListener('mousedown', this.onWindowMouseDown)
                document.removeEventListener('keydown', this.onKeyDown)
            }
            if (this.props.waitingSurfacesInteraction) {
                this.setState({
                    isInvalidForStackable: false,
                })
            }
        }
    }

    componentWillUnmount() {
        const canvas = this.canvasRef.current
        canvas.removeEventListener('webglcontextlost', this.props.onContextLost, false)
        canvas.removeEventListener('webglcontextcreationerror', this.onContextCreationError, false)
        canvas.removeEventListener('webglcontextrestored', this.props.onContextRestored, false)

        this.props.destroyRenderer()
    }

    onContextCreationError(evt) {
        Sentry.captureEvent({
            message: `GL Context creation error`,
            breadcrumbs: [
                {
                    type: `Error message`,
                    category: SENTRY_BREADCRUMB_CATEGORY,
                    data: {
                        errorMessage: (evt && evt.statusMessage) || `Unknown error`,
                    },
                },
            ],
        })

        const { onContextCreationError } = this.props
        onContextCreationError()
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const { composition, debugSettings } = nextProps

        return {
            ...prevState,
            debugUseMultiShadow: debugSettings.useMultiShadow,
            composition,
        }
    }

    onKeyDown(e) {
        const { waitingSurfacesInteraction, hideSurfaces } = this.props
        if (waitingSurfacesInteraction && e.keyCode === 27) {
            hideSurfaces()
        }
    }

    onWindowMouseDown() {
        const { waitingSurfacesInteraction, hideSurfaces } = this.props
        if (waitingSurfacesInteraction) {
            hideSurfaces()
        }
    }

    onMouseDown(e) {
        const { waitingSurfacesInteraction, onStackableSurfaceClicked } = this.props
        const { composition } = this.state
        const canStack = composition && composition.stackableSurfaces && composition.stackableSurfaces.length
        const renderer = getRenderer()
        if (waitingSurfacesInteraction && canStack) {
            e.stopPropagation()

            const { relX, relY, x, y } = this.getRelativeCoords(e)
            const planeId = renderer.getStackableSurfaceAtPoint(relX, relY)
            if (planeId === null) {
                this.setState({ isInvalidForStackable: true })
            } else {
                this.setState({ isInvalidForStackable: false })
                onStackableSurfaceClicked(planeId, x, y)
            }
        } else if (!this.state.underCursorItemId) {
            this.props.clearSelection()
        }
    }

    onMouseLeave() {
        this.setState({
            underCursorItemId: null,
            underCursorStackableSurfaceId: null,
            isInvalidForStackable: false,
        })
    }

    onSweepButtonMouseOver() {
        this.setState({
            revealDots: true,
        })
    }

    onSweepButtonMouseOut() {
        this.setState({
            revealDots: false,
        })
    }

    getRelativeCoords(mouseEvt) {
        const renderer = getRenderer()
        if (!renderer) {
            return {
                relX: 0,
                relY: 0,
                x: 0,
                y: 0,
            }
        }

        const corner = this.sceneContainerRef.current
        const { clientX, clientY } = getClientEventCoords(mouseEvt)
        const { left, top, width, height } = corner.getBoundingClientRect()

        const eventX = clientX - left
        const eventY = clientY - top

        const relX = eventX / width
        const relY = eventY / height

        const renderDimensions = renderer.getRenderDimensions()
        const scaleX = renderDimensions ? renderDimensions[0] / width : 1.0
        const scaleY = renderDimensions ? renderDimensions[1] / height : 1.0

        return {
            relX,
            relY,
            x: eventX * scaleX,
            y: eventY * scaleY,
        }
    }

    triggerRaycast(e) {
        const { relX, relY, x, y } = this.getRelativeCoords(e)
        const { waitingSurfacesInteraction } = this.props
        const renderer = getRenderer()

        if (renderer) {
            let underCursorStackableSurfaceId = null
            let underCursorItemId = null

            if (waitingSurfacesInteraction) {
                underCursorStackableSurfaceId = renderer.getStackableSurfaceAtPoint(relX, relY)
            }
            if (underCursorStackableSurfaceId !== this.state.underCursorStackableSurfaceId) {
                this.setState({ underCursorStackableSurfaceId, isInvalidForStackable: false })
            }
            if (underCursorStackableSurfaceId !== null) {
                this.setState({ stackableCursorMouseoverCoords: { x, y } })
            }

            if (underCursorStackableSurfaceId === null) {
                underCursorItemId = renderer.getObjectIdAtPoint(relX, relY)
            }
            if (underCursorItemId !== this.state.underCursorItemId) {
                this.setState({ underCursorItemId })
            }
        }
    }

    cacheImages(prevState) {
        // TODO: this caching only works for artItems
        if (!this.props.composition) {
            return
        }
        if (
            !prevState ||
            !prevState.composition ||
            prevState.composition.renderItems !== this.state.composition.renderItems
        ) {
            const {
                composition: { renderItems },
            } = this.props

            const cache = (renderItem) => {
                const { id } = renderItem
                if (renderItem.renderType === RenderItemTypes.ART_ITEM && !this.state[id]) {
                    const imageUrl =
                        Array.isArray(renderItem.artAssetUrls) &&
                        renderItem.artAssetUrls.length > 0 &&
                        renderItem.artAssetUrls[0]
                    if (imageUrl) {
                        Preview.generateImageData(imageUrl).then((artImageData) => {
                            this.setState({ [id]: artImageData })
                        })
                    }
                }
            }

            renderItems.forEach(cache)
        }
    }

    render() {
        const {
            customContext,
            sceneFetchState,
            isLoggedIn,
            canvasHeight,
            canvasWidth,
            settings,
            t,
            previewSettings,
            waitingSurfacesInteraction,
            removeRenderItems,
            undoRemoveRenderItems,
            correctOrientation,
            onCloseMagicWindow,
            hasRendered,
            wallDimensions,
            showMissingWallsModal,
            closeMissingWallsModal,
            setShowDesignSummary,
            placeStackableOnFloor,
            debugSettings,
        } = this.props
        const { isInvalidForStackable, revealDots } = this.state

        const renderer = getRenderer()
        const { isMagicWindow, isMobile } = this.context
        const { hidePins: adminHidePins } = debugSettings

        const sceneHeight = wallDimensions.width > 0 ? `${(wallDimensions.height / wallDimensions.width) * 100}%` : 0

        const { composition, underCursorItemId, underCursorStackableSurfaceId, stackableCursorMouseoverCoords } =
            this.state

        const key = composition && composition.scene ? composition.scene.sceneId : null
        const thumbUrl = composition && composition.scene ? composition.scene.thumbUrl : null

        let renderItems = []

        if (composition && composition.renderItems) {
            renderItems = composition.renderItems
        }

        let stackableSurfaces = []
        if (composition && composition.stackableSurfaces) {
            stackableSurfaces = composition.stackableSurfaces
        }

        const hasWallPaint = !isMobile && composition && composition.scene && composition.scene.hasWallPaint
        const hasError = sceneFetchState === STATE_ERROR

        let errorMessage

        if (sceneFetchState === STATE_ERROR) {
            errorMessage = (
                <div>
                    <Trans i18nKey="design.failed to load scene error message">
                        We&apos;re sorry, but this is unexpected. We were not able to load your rooms.
                        <br />
                        <br />
                        Please reload the page, or try again later.
                    </Trans>
                </div>
            )
        } else if (isLoggedIn) {
            errorMessage = (
                <div>
                    <Trans i18nKey="design.no access error message">
                        Your account does not have access to this room.
                        <br />
                        Please visit your Dashboard to access your rooms.
                    </Trans>
                </div>
            )
        } else {
            errorMessage = (
                <div>
                    <p>{t('design.need to login')}</p>
                </div>
            )
        }

        let stackableIndicatorMessage = null

        if (isInvalidForStackable) {
            stackableIndicatorMessage = (
                <Trans i18nKey="design.choose a valid surface">Please choose a valid surface</Trans>
            )
        } else if (underCursorStackableSurfaceId) {
            stackableIndicatorMessage = (
                <Trans i18nKey="design.click to select surface">Click to select this surface</Trans>
            )
        } else {
            stackableIndicatorMessage = (
                <Trans i18nKey="design.select a surface with floor">
                    Select a colored surface or{' '}
                    <button
                        onMouseDown={stopPropagationFunc}
                        className="placeFloorAction"
                        onClick={placeStackableOnFloor}
                    >
                        <span>place on floor</span>
                    </button>
                </Trans>
            )
        }

        const ssVizUrl = composition && composition.scene && composition.scene.manifest.semantic_segmentation_visual_0

        const showSurfacesMessage = stackableSurfaces.length === 0 && waitingSurfacesInteraction
        const hasRenderItems = !!(composition && composition.renderItems && composition.renderItems.length)

        const isMobileWebMagicWindow = isMagicWindow && isMobile

        return (
            <div
                className={classNames('Preview', { hasRendered, mobile: isMobile, correctOrientation, adminHidePins })}
                key={key}
                style={{ backgroundImage: `url(${thumbUrl})` }}
            >
                <SkipLinks skipLinks={[BROWSE, HEADER, NAVIGATION]} skipContext={DESIGN} />
                {hasError && <div className="errorContainer">{errorMessage}</div>}

                {!hasRendered && !hasError && (
                    <div className="loadingContainer">
                        <div className="loadingContentContainer">
                            <p>{t('design.preparing your room')}</p>
                            <div className="ballContainer">
                                <BouncingBallLoader />
                            </div>
                        </div>
                    </div>
                )}

                {this.props.debugSettings.ssViz ? <img src={ssVizUrl} className="ssViz" alt="" /> : null}
                {composition && composition.scene ? (
                    <div
                        className={classNames('sceneContainer', { waitingSurfacesInteraction })}
                        style={{
                            paddingTop: sceneHeight,
                        }}
                        ref={this.sceneContainerRef}
                        onMouseMove={this.triggerRaycast}
                        onTouchStart={this.triggerRaycast}
                        onMouseLeave={this.onMouseLeave}
                        onMouseDown={this.onMouseDown}
                    >
                        {!isMagicWindow && showSurfacesMessage && (
                            <MissingFeatureModal
                                body={t('design.did not find a surface for accessory')}
                                headerText={t('design.place accessory on floor')}
                                ctaText={t('design.place on floor')}
                                closeBtnText={t('design.cancel')}
                                onCtaClick={placeStackableOnFloor}
                            />
                        )}
                        {showMissingWallsModal && (
                            <MissingFeatureModal onClose={closeMissingWallsModal} body={t('design.capture walls')} />
                        )}

                        {isMagicWindow && !isMobile && (
                            <Sweep
                                compositionId={composition.id}
                                removeRenderItems={removeRenderItems}
                                undoRemoveRenderItems={undoRemoveRenderItems}
                                hasRenderItems={hasRenderItems}
                                onSweepButtonMouseOut={this.onSweepButtonMouseOut}
                                onSweepButtonMouseOver={this.onSweepButtonMouseOver}
                            />
                        )}

                        {isMobileWebMagicWindow && (
                            <>
                                <button className="closeMWButton" onClick={onCloseMagicWindow}>
                                    <SSRIcon paths={cross} />
                                </button>
                                <button className="nextArrow" onClick={setShowDesignSummary}>
                                    <SSRIcon paths={arrowRight} />
                                </button>
                            </>
                        )}

                        {!showSurfacesMessage ? (
                            <div
                                className={classNames('stackableIndicator', {
                                    waitingSurfacesInteraction,
                                    isInvalidForStackable,
                                })}
                            >
                                <p>{stackableIndicatorMessage}</p>
                            </div>
                        ) : null}
                        {settings.magicEraserMode && <DirectInteractionLayer />}
                        {renderer && settings.magicEraserMode && (
                            <div className="magicEraserHideAllContainer">
                                <Button
                                    onClick={() => {
                                        renderer.setStaticFurnitureViz(false)
                                    }}
                                    text={t('design.hide all')}
                                    ssrIcon={visibilityHide}
                                    small
                                />
                                <Button
                                    onClick={() => {
                                        renderer.setStaticFurnitureViz(true)
                                    }}
                                    text={t('design.show all')}
                                    ssrIcon={visibilityShow}
                                    small
                                />
                            </div>
                        )}
                        {renderer
                            ? renderItems.map((renderItem) => {
                                  const isUnderCursor = renderItem.uniqueId === underCursorItemId
                                  return (
                                      <CompositionRenderItemContainer
                                          renderItem={renderItem}
                                          key={`${renderItem.uniqueId}`}
                                          compositionId={composition.id}
                                          isUnderCursor={isUnderCursor}
                                          customContext={customContext}
                                          artImageData={this.state[renderItem.id]}
                                          revealDots={revealDots}
                                      />
                                  )
                              })
                            : null}
                        {renderer
                            ? stackableSurfaces.map((plane) => {
                                  const highlight = underCursorStackableSurfaceId === plane.id
                                  const coords = highlight ? stackableCursorMouseoverCoords : null
                                  return (
                                      <CompositionStackableSurfaceItemContainer
                                          key={`${plane.id}`}
                                          plane={plane}
                                          visible={previewSettings.showSurfaces.includes(plane.id)}
                                          highlight={highlight}
                                          parent={plane.parent}
                                          mouseOverCoords={coords}
                                      />
                                  )
                              })
                            : null}
                        {hasWallPaint && (
                            <WallPaintPinContainer
                                wallWidth={wallDimensions.width}
                                wallHeight={wallDimensions.height}
                            />
                        )}
                    </div>
                ) : null}
                <canvas id="PreviewCanvas" width={canvasWidth} height={canvasHeight} ref={this.canvasRef} />
                {/* Note the memory charts waits for the renderer to be initialized to use the GlMemoryRecorder */}
                {hasRendered && this.props.debugSettings.showMemoryStats && (
                    <MemoryCharts debugSettings={this.props.debugSettings} />
                )}
            </div>
        )
    }
}

Preview.contextType = SugarcubeContext
Preview.defaultProps = {
    composition: null,
    correctOrientation: false,
    maxTextureSize: null,
    onCloseMagicWindow: null,
}

Preview.propTypes = {
    hideSurfaces: func.isRequired,
    composition: compositionType,
    customContext: string.isRequired,
    canvasWidth: number.isRequired,
    maxTextureSize: number,
    canvasHeight: number.isRequired,
    sceneFetchState: string.isRequired,
    isLoggedIn: bool.isRequired,
    debugSettings: debugSettingsType.isRequired,
    previewSettings: previewSettingsType.isRequired,
    clearSelection: func.isRequired,
    newRenderer: func.isRequired,
    destroyRenderer: func.isRequired,
    onRendererReady: func.isRequired,
    onWallDataReady: func.isRequired,
    onFirstRender: func.isRequired,
    settings: previewSettingsType.isRequired,
    t: func.isRequired,
    onStackableSurfaceClicked: func.isRequired,
    waitingSurfacesInteraction: bool.isRequired,
    onUpdateCompositionSaveData: func.isRequired,
    removeRenderItems: func.isRequired,
    undoRemoveRenderItems: func.isRequired,
    hasRendered: bool.isRequired,
    correctOrientation: bool,
    onCloseMagicWindow: func,
    wallDimensions: dimensionsType.isRequired,
    showMissingWallsModal: bool.isRequired,
    closeMissingWallsModal: func.isRequired,
    onContextLost: func.isRequired,
    onContextCreationError: func.isRequired,
    onContextRestored: func.isRequired,
    onInitializationFailure: func.isRequired,
    setShowDesignSummary: func.isRequired,
    placeStackableOnFloor: func.isRequired,
    updateHiddenSurfaces: func.isRequired,
}

export default withTranslation()(Preview)
